/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: mmc-inventory-modal-component.ts
 *
 * Created by randysorensen on 2019-05-09.
 */


function MMCInventoryModalController(
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    uiGridConstants: JS5Object,
    ModalService: JS5Object,
    NotificationService: JS5Object,
    PartnerPackagesService: JS5Object
) {

    const getComponentInventory = () => {
        let packageId = this.resolve.packageId;
        let partnerId = this.resolve.partnerId;

        this.loadingIndicator.show = true;
        return PartnerPackagesService.getComponentInventory(packageId, partnerId)
            .then((componentInventory: JS5Object[]) => {
                return componentInventory;
            }).catch((error: JS5Object) => {
                NotificationService.createFailureNotification(
                    `Failed to receive MMC inventory`,
                    _.get(error, 'data.message')
                );
                this.close({});
            }).finally(() => {
                this.loadingIndicator.show = false;
            });
    };

    const setInventoryObjectDirty = (id: string) => {
        this.dirtyInventoryIds.push(id);
    };

    const getModifiedInventories = () => {
        return _.filter(this.componentInventory, (inventoryObject: JS5Object) => {
            return _.includes(this.dirtyInventoryIds, inventoryObject.id);
        });
    };


    //======//
    // Grid //
    //======//

    const setupGrid = () => {
        const colDefs = [
            {
                // Active Checkbox
                cellTemplate: 'package/editor/components/view/distributionGrid/mmcInventoryModal/cellTemplates/check-mark-template.html',
                headerCellTemplate: 'package/editor/components/view/distributionGrid/mmcInventoryModal/cellTemplates/check-mark-header-template.html',
                displayName: 'Active',
                enableCellEdit: false,
                field: 'active',
                width: '70',
            },
            {
                // Inventory ID
                // Note: This function takes priority over enableCellEdit
                cellEditableCondition: () => !this.readOnly,
                cellTemplate: 'package/editor/components/view/distributionGrid/mmcInventoryModal/cellTemplates/inline-edit-template.html',
                displayName: 'Inventory ID',
                enableCellEdit: true,
                field: 'inventoryId',
                width: '750',
            },
            {
                // Type
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
                displayName: 'Type',
                enableCellEdit: false,
                field: 'type',
                width: '90'
            },
            {
                // Filename
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html',
                displayName: 'Filename',
                enableCellEdit: false,
                enableSorting: true,
                field: 'filename',
                sort: {
                    direction: uiGridConstants.ASC,
                    priority: 1,
                },
                width: '600',
            },
            {
                // Date Delivered
                cellTemplate: 'components/visual/grid/date-time-column-template.html',
                displayName: 'Date Delivered',
                enableCellEdit: false,
                enableSorting: true,
                field: 'dateDelivered',
                sort: {
                    direction: uiGridConstants.ASC,
                    priority: 1,
                },
                width: '185',
            }
        ];

        this.gridOptions = {
            columnDefs: colDefs,
            data: [],
            enableColumnMenus: false,
            enableRowHeaderSelection: false,
            enableRowSelection: true,
            enableSelectAll: false,
            enableSorting: false,
            enableHorizontalScrollbar: uiGridConstants.ALWAYS,
            enableVerticalScrollbar: uiGridConstants.ALWAYS,
            multiSelect: false,
            onRegisterApi: (gridApi: JS5Object) => {
                this.gridApi = gridApi;
                this.gridApi.edit.on.afterCellEdit(null,(rowEntity: JS5Object) => {
                    setInventoryObjectDirty(rowEntity.id);
                });
            },
            rowTemplate: 'components/visual/grid/searchview-component-row-template.html'
        };
    };

    //================//
    // Template Logic //
    //================//

    this.saveButtonDisabled = () => {
        let disabled = false;
        _.forEach(this.componentInventory, (inventoryItem: JS5Object) => {
            disabled = disabled || _.size(inventoryItem.inventoryId) === 0;
        });
        disabled = disabled || this.dirtyInventoryIds.length === 0;

        return disabled;
    };

    this.onSaveClicked = () => {
        let modifiedInventories = getModifiedInventories();

        let inventorySavePromises = <JS5Promise[]>[];
        _.forEach(modifiedInventories, (inventoryToSave: JS5Object) => {
            inventorySavePromises.push(PartnerPackagesService.updateComponentInventory(inventoryToSave));
        });

        this.loadingIndicator.show = true;
        return $q.all(inventorySavePromises)
            .then(() => {
                this.close({});
            }).catch((error: JS5Object) => {
                NotificationService.createFailureNotification(
                    `Failed to update MMC inventory`,
                    _.get(error, 'data.message')
                );
            }).finally(() => {
                this.loadingIndicator.show = false;
            });
    };

    const showUnsavedChangesNotification = (resultHandler: JS5Function) => {
        ModalService.showMessageModal({
            messageText: 'You have unsaved changes. Are you sure you wish to cancel?',
            pullCancelLeft: true,
            title: 'Discard Unsaved Changes?',
            showOK: true,
            okText: 'Yes',
            windowClass: 'confirm-modal-sm'
        }, resultHandler);
    };

    this.onCancelClicked = () => {
        if (getModifiedInventories().length) {
            showUnsavedChangesNotification(() => {
                this.close({});
            });
        } else {
            this.close({});
        }
    };

    this.areAllRowsActive = () => {
        return _.every(this.gridOptions.data, 'active');
    };

    this.onCheckMarkHeaderClicked = ($event: JS5Object) => {
        let wasAllRowsActive = this.areAllRowsActive();
        $event.stopImmediatePropagation();
        $event.target.blur();

        _.forEach(this.gridOptions.data, (rowEntity: JS5Object) => {
            rowEntity.active = !wasAllRowsActive;
            setInventoryObjectDirty(rowEntity.id);
        });
    };

    this.onCheckMarkCellClicked = (row: JS5Object, $event: JS5Object) => {
        $event.stopImmediatePropagation();
        $event.target.blur();

        row.entity.active = !row.entity.active;
        setInventoryObjectDirty(row.entity.id);
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };
        this.readOnly = this.resolve.readOnly;
        this.gridOptions = {};
        this.dirtyInventoryIds = <string[]>[];

        setupGrid();
        getComponentInventory()
            .then((componentInventory: JS5Object[]) => {
                this.componentInventory = componentInventory;
                this.gridOptions.data = componentInventory;
            });
    };

}

angular.module('prism-ui.package').component(
    'mmcInventoryModal',
    {
        bindings: {
            close: '&',
            dismiss: '&',
            resolve: '<'
        },
        controller: MMCInventoryModalController,
        templateUrl: 'package/editor/components/view/distributionGrid/mmcInventoryModal/mmc-inventory-modal-template.html'
    });
